import React from 'react'
import { Route, Switch, useRouteMatch, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { createTheme, ThemeProvider } from '@material-ui/core/styles'
import AppSnackbarProvider from 'components/app-snackbar-provider'
import MessageOutlinedIcon from 'components/material-symbol/icons/Message'
import SchoolOutlinedIcon from 'components/material-symbol/icons/School'
import HomeIcon from 'components/material-symbol/icons/Home'
import OpenInNewIcon from 'components/material-symbol/icons/OpenInNew'
import ConfirmationNumberOutlinedIcon from 'components/material-symbol/icons/ConfirmationNumber'
import CelebrationOutlinedIcon from 'components/material-symbol/icons/Celebration'
import VideocamIcon from 'components/material-symbol/icons/Videocam'
import Shell from 'components/app-shell'
import PGAIcon from 'images/pga-logo-navy.png'
import { withProtectedRoute, roles } from 'lib/auth'
import themes, { extendTheme } from 'themes'
import ConsumerMessages from 'pages/club/messages'
import MyCoach from 'pages/club/my-coach'
import MyEvents from 'pages/club/my-events'
import MyLessonPacks from 'pages/club/my-lesson-packs'
import MyPgaBannerAd from 'components/smart-banner-ads/my-pga-banner-ad'
import { useMyVirtualCoachingRequests } from 'pages/virtual-coaching-student/api-hooks'
import VirtualCoachingRequests from 'pages/virtual-coaching-student/requests-list'
import VirtualCoachingRequestDetail from 'pages/virtual-coaching-student/request-detail'
import VirtualCoachingUpload from 'pages/virtual-coaching-student/upload-page'
import GiftLessonPack from 'pages/club/my-lesson-packs/gift'
import AcceptLessonCredit from 'pages/club/my-lesson-packs/accept-lesson-credit'
import { useUnreadMessagesCount } from 'lib/stream-messaging/hooks'
import InlineCountBadge from 'components/inline-count-badge'
import { useIsMobile } from 'lib/hooks'
import { useDefaultLauncherVisibility } from 'lib/support'
import { useVirtualCoachingFlag } from 'lib/pga-coach/use-virtual-coaching'

const ClubRoutes = () => {
  const { path } = useRouteMatch()
  const { pathname } = useLocation()
  const isMobile = useIsMobile()
  const unreadMessagesCount = useUnreadMessagesCount()
  // The golfer pages share the coach-side virtual coaching flag. With it off,
  // /club behaves as before: no request query, no nav item, no routes.
  const isVirtualCoachingEnabled = useVirtualCoachingFlag()
  // "Virtual Coaching" shows once the golfer has bought one. While loading, or
  // when the query fails (backend not deployed), it is hidden. The list page
  // reads the same cached query.
  const { data: virtualCoachingData } = useMyVirtualCoachingRequests({
    skip: !isVirtualCoachingEnabled,
  })
  const hasVirtualCoachingRequests =
    isVirtualCoachingEnabled &&
    (virtualCoachingData?.myVirtualCoachingRequests?.length ?? 0) > 0

  const shouldShowDefaultLauncher =
    !isMobile &&
    !pathname.includes('/messages') &&
    !pathname.includes('/accept-lesson-credit')

  useDefaultLauncherVisibility(shouldShowDefaultLauncher)

  const menuItems = [
    {
      icon: <HomeIcon />,
      label: 'Dashboard',
      path: `${path}`,
      exact: true,
      tooltip: 'My Coach',
    },
    {
      icon: <ConfirmationNumberOutlinedIcon size={1} />,
      label: 'Lesson Packs',
      path: `${path}/lesson-packs`,
      tooltip: 'Lesson Packs',
    },
    {
      icon: <VideocamIcon />,
      label: 'Virtual Coaching',
      path: `${path}/virtual-coaching`,
      tooltip: 'Virtual Coaching',
      hide: !hasVirtualCoachingRequests,
    },
    {
      icon: <CelebrationOutlinedIcon />,
      label: 'My Events',
      path: `${path}/events`,
      tooltip: 'My Events',
    },
    {
      icon: <MessageOutlinedIcon />,
      label: (
        <InlineCountBadge count={unreadMessagesCount}>
          Messaging
        </InlineCountBadge>
      ),
      path: `${path}/messages`,
      tooltip: 'Messaging',
    },
    {
      icon: <SchoolOutlinedIcon />,
      label: 'Golf Tips',
      path: 'https://www.pga.com/search?searchText=golf%20tips&offset=0',
      external: true,
      annotation: <OpenInNewIcon style={{ fontSize: '16px' }} />,
      tooltip: 'Golf Tips',
    },
  ]

  return (
    <ThemeProvider
      theme={extendTheme(themes.pgaCoach)(createTheme(themes.common))}
    >
      <Helmet>
        <title>MyPGA Clubhouse</title>
        <meta name="description" content="MyPGA Clubhouse" />
        <link rel="icon" href="/pga-coach-favicon.ico" />
      </Helmet>
      <AppSnackbarProvider
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MyPgaBannerAd />
        <Shell
          logo={{
            src: PGAIcon,
            alt: 'PGA logo',
            width: '48px',
            mobileWidth: '48px',
          }}
          menuItems={menuItems}
          showBrandSwitcher={false}
        >
          <Switch>
            <Route exact path={`${path}`} component={MyCoach} />
            <Route
              exact
              path={`${path}/lesson-packs`}
              component={MyLessonPacks}
            />
            {isVirtualCoachingEnabled && (
              <Route
                exact
                path={`${path}/virtual-coaching`}
                component={VirtualCoachingRequests}
              />
            )}
            {isVirtualCoachingEnabled && (
              <Route
                exact
                path={`${path}/virtual-coaching/:requestId/upload`}
                component={VirtualCoachingUpload}
              />
            )}
            {isVirtualCoachingEnabled && (
              <Route
                exact
                path={`${path}/virtual-coaching/:requestId`}
                component={VirtualCoachingRequestDetail}
              />
            )}
            <Route
              exact
              path={`${path}/events/:eventSlug?`}
              component={MyEvents}
            />
            <Route
              exact
              path={`${path}/messages/:externalId/new-conversation`}
              component={ConsumerMessages}
            />
            <Route
              exact
              path={`${path}/messages/:channelId?`}
              component={ConsumerMessages}
            />
            <Route
              exact
              path={`${path}/lesson-packs/:lessonPackId/gift`}
              component={GiftLessonPack}
            />
            <Route
              exact
              path={`${path}/lesson-packs/:lessonPackId/accept-lesson-credit`}
              component={AcceptLessonCredit}
            />
          </Switch>
        </Shell>
      </AppSnackbarProvider>
    </ThemeProvider>
  )
}

export default withProtectedRoute(ClubRoutes, roles.CONSUMER)
